import type { StandardSchemaV1 } from "@standard-schema/spec";
import type { CollectionConfig } from "@tanstack/db";
import {
  queryCollectionOptions,
  type QueryCollectionConfig,
  type QueryCollectionUtils,
} from "@tanstack/query-db-collection";
import type {
  QueryClient,
  QueryFunctionContext,
  QueryKey,
} from "@tanstack/react-query";
import {
  applySupportTicketHydrationQueryClientDefaults,
  supportTicketHydrationQueryRetryOptions,
} from "./support-ticket-hydration-query";

type Recoverable<T> = T & { recoverTransientFailures?: boolean };

type InferSchemaOutput<T> = T extends StandardSchemaV1
  ? StandardSchemaV1.InferOutput<T> extends object
    ? StandardSchemaV1.InferOutput<T>
    : Record<string, unknown>
  : Record<string, unknown>;

type InferSchemaInput<T> = T extends StandardSchemaV1
  ? StandardSchemaV1.InferInput<T> extends object
    ? StandardSchemaV1.InferInput<T>
    : Record<string, unknown>
  : Record<string, unknown>;

type QueryCollectionQueryFn<
  TQueryKey extends QueryKey = QueryKey,
  TData = unknown,
> = (context: QueryFunctionContext<TQueryKey>) => TData;

export function supportTicketQueryCollectionOptions<
  T extends StandardSchemaV1,
  TQueryFn extends QueryCollectionQueryFn,
  TError = unknown,
  TQueryKey extends QueryKey = QueryKey,
  TKey extends string | number = string | number,
  TQueryData = Awaited<ReturnType<TQueryFn>>,
>(
  config: Recoverable<
    QueryCollectionConfig<
      InferSchemaOutput<T>,
      TQueryFn,
      TError,
      TQueryKey,
      TKey,
      T
    > & { schema: T; select: (data: TQueryData) => InferSchemaInput<T>[] }
  >,
): CollectionConfig<
  InferSchemaOutput<T>,
  TKey,
  T,
  QueryCollectionUtils<InferSchemaOutput<T>, TKey, InferSchemaInput<T>, TError>
> & {
  schema: T;
  utils: QueryCollectionUtils<
    InferSchemaOutput<T>,
    TKey,
    InferSchemaInput<T>,
    TError
  >;
};

export function supportTicketQueryCollectionOptions<
  T extends object,
  TQueryFn extends QueryCollectionQueryFn = QueryCollectionQueryFn,
  TError = unknown,
  TQueryKey extends QueryKey = QueryKey,
  TKey extends string | number = string | number,
  TQueryData = Awaited<ReturnType<TQueryFn>>,
>(
  config: Recoverable<
    QueryCollectionConfig<
      T,
      TQueryFn,
      TError,
      TQueryKey,
      TKey,
      never,
      TQueryData
    > & { schema?: never; select: (data: TQueryData) => T[] }
  >,
): CollectionConfig<
  T,
  TKey,
  never,
  QueryCollectionUtils<T, TKey, T, TError>
> & { schema?: never; utils: QueryCollectionUtils<T, TKey, T, TError> };

export function supportTicketQueryCollectionOptions<
  T extends StandardSchemaV1,
  TError = unknown,
  TQueryKey extends QueryKey = QueryKey,
  TKey extends string | number = string | number,
>(
  config: Recoverable<
    QueryCollectionConfig<
      InferSchemaOutput<T>,
      QueryCollectionQueryFn<
        TQueryKey,
        InferSchemaOutput<T>[] | Promise<InferSchemaOutput<T>[]>
      >,
      TError,
      TQueryKey,
      TKey,
      T
    > & { schema: T }
  >,
): CollectionConfig<
  InferSchemaOutput<T>,
  TKey,
  T,
  QueryCollectionUtils<InferSchemaOutput<T>, TKey, InferSchemaInput<T>, TError>
> & {
  schema: T;
  utils: QueryCollectionUtils<
    InferSchemaOutput<T>,
    TKey,
    InferSchemaInput<T>,
    TError
  >;
};

export function supportTicketQueryCollectionOptions<
  T extends object,
  TError = unknown,
  TQueryKey extends QueryKey = QueryKey,
  TKey extends string | number = string | number,
>(
  config: Recoverable<
    QueryCollectionConfig<
      T,
      QueryCollectionQueryFn<TQueryKey, T[] | Promise<T[]>>,
      TError,
      TQueryKey,
      TKey
    > & { schema?: never }
  >,
): CollectionConfig<
  T,
  TKey,
  never,
  QueryCollectionUtils<T, TKey, T, TError>
> & { schema?: never; utils: QueryCollectionUtils<T, TKey, T, TError> };

export function supportTicketQueryCollectionOptions(config: {
  recoverTransientFailures?: boolean;
  queryClient: QueryClient;
  queryKey: unknown;
}): never {
  const { recoverTransientFailures = true, ...options } = config;
  if (recoverTransientFailures) {
    if (Array.isArray(config.queryKey)) {
      applySupportTicketHydrationQueryClientDefaults(
        config.queryClient,
        config.queryKey,
      );
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    return queryCollectionOptions({
      ...options,
      retry: supportTicketHydrationQueryRetryOptions.retry,
      retryDelay: supportTicketHydrationQueryRetryOptions.retryDelay,
      refetchInterval: supportTicketHydrationQueryRetryOptions.refetchInterval,
    } as never) as never;
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return queryCollectionOptions(options as never) as never;
}
